<template>
  <div
    class="mobile-i18n-page home"
    :class="{ 'home--english': $store.state.locale.uiLocale === 'en-US' }"
    ref="homeRef"
    :style="{
      '--custom-bg-height': `${logoList[1]?.remark || 0}px`,
    }"
  >
    <van-popup
      v-if="hasClueViewPermission"
      v-model="showImportDialog"
      round
      position="bottom"
      safe-area-inset-bottom
      get-container="body"
      :z-index="3000"
      :overlay="false"
      :lock-scroll="false"
      close-on-popstate
      class="import-clue-popup"
    >
      <div class="import-clue-dialog">
        <div class="import-clue-dialog__handle" aria-hidden="true"></div>
        <div class="import-clue-dialog__heading">
          <span class="import-clue-dialog__icon">
            <van-icon name="upgrade" />
          </span>
          <div>
            <!-- 批量上传供需信息 -->
            <h2>{{ $t("mobile.home.bulkUploadTitle") }}</h2>
            <!-- 支持 XLS、XLSX 文件，每次上传一个文件 -->
            <p>{{ $t("mobile.home.bulkUploadHint") }}</p>
          </div>
        </div>
        <!-- 点击上传 -->
        <van-uploader
          :after-read="handleFileUpload"
          :before-delete="beforeDeleteFile"
          v-model="importFileList"
          multiple
          :max-count="1"
          accept=".xlsx,.xls"
          :upload-text="$t('mobile.home.uploadAction')"
          class="import-clue-uploader"
        >
        </van-uploader>
        <div class="upload-actions">
          <!-- 取消 -->
          <van-button block @click="handleCancel">{{
            $t("mobile.home.cancel")
          }}</van-button>
          <!-- 确定 -->
          <van-button
            type="primary"
            block
            :loading="isImportSubmitting"
            @click="submitImport"
          >
            {{ $t("mobile.home.confirm") }}
          </van-button>
        </div>
      </div>
    </van-popup>

    <div class="main-container">
      <div class="home-upper">
        <van-image :src="logoList[1].image" class="home-bg" fit="cover" />
        <div class="home-upper__content">
          <div class="home-brand-space">
            <van-image
              :src="mobileFeaturedLogoSrc"
              :alt="mobileFeaturedLogoAlt"
              class="xsou-title"
            />
          </div>
          <div class="home-search-slot">
            <!-- 搜索料号、名称 -->
            <!-- 先搜一下 -->
            <SearchBox
              :placeholder="$t('common.searchPlaceholder')"
              :button-text="$t('common.searchAction')"
              input-bg-color="rgba(255, 255, 255, 0.9)"
              button-bg-color="#4286F5"
              button-text-color="#ffffff"
              shape="round"
              :class="searchBoxClass"
              :isFixed="isFixed"
              @search="onProductSearch"
            />
          </div>
          <van-swipe
            v-if="bannerList.length"
            class="my-swipe"
            :autoplay="3000"
            indicator-color="white"
          >
            <van-swipe-item v-for="item in bannerList" :key="item.id">
              <!-- 首页 Banner -->
              <img
                :src="item.image"
                :alt="item.title || $t('mobile.home.bannerAlt')"
              />
            </van-swipe-item>
          </van-swipe>

          <!-- 先搜服务价值 -->
          <div
            class="home-value-strip"
            :aria-label="$t('mobile.home.serviceValues')"
          >
            <div>
              <img :src="require('@/img/home/1.png')" alt="" />
              <!-- 卓越服务 -->
              <span>{{ $t("mobile.home.excellentService") }}</span>
            </div>
            <div>
              <img :src="require('@/img/home/2.png')" alt="" />
              <!-- 持续创造 -->
              <span>{{ $t("mobile.home.continuousInnovation") }}</span>
            </div>
            <div>
              <img :src="require('@/img/home/3.png')" alt="" />
              <!-- 协作共赢 -->
              <span>{{ $t("mobile.home.winTogether") }}</span>
            </div>
            <div>
              <img :src="require('@/img/home/4.png')" alt="" />
              <!-- 价值共享 -->
              <span>{{ $t("mobile.home.sharedValue") }}</span>
            </div>
          </div>

          <!-- <section
            :class="[
              'brand-showcase',
              { 'brand-showcase--can-scroll-right': canScrollBrandsRight },
            ]"
            aria-label="热门品牌"
          >
            <div
              ref="brandViewport"
              class="brand-showcase__viewport"
              @scroll.passive="updateBrandScrollState"
            >
              <button
                v-for="(item, index) in brandShowcaseList"
                :key="`${getBrandName(item)}-${index}`"
                class="brand-showcase__item"
                type="button"
                :aria-label="`搜索${getBrandName(item)}品牌商品`"
                @click="openBrand(item)"
              >
                <img
                  v-if="item.logo"
                  :src="item.logo"
                  :alt="getBrandName(item)"
                />
                <span v-else>{{ getBrandInitial(item) }}</span>
              </button>
            </div>
          </section> -->

          <div class="grid-container" v-if="navList && navList.length > 0">
            <div
              v-for="(item, index) in navList"
              :key="`${item.imgUrl}-${index}`"
              class="grid-item"
              :class="{ 'grid-item--disabled': !isEnabledHomeNav(item) }"
              :aria-disabled="isEnabledHomeNav(item) ? 'false' : 'true'"
              @click="onHomeNavClick(item)"
            >
              <div class="icon-wrapper">
                <img :src="item.imgUrl" alt="" class="grid-icon" />
              </div>
              <div class="grid-text">{{ displayShortcutTitle(item) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 首页内容 -->
      <section class="home-showcase" :aria-label="$t('mobile.home.content')">
        <div
          :class="[
            'home-feed',
            { 'home-feed--standalone': !hasClueViewPermission },
          ]"
        >
          <div v-if="tabs.length > 1" class="home-tab-bar">
            <!-- 首页信息分类 -->
            <nav
              class="home-tab-items"
              role="tablist"
              :aria-label="$t('mobile.home.feedTabs')"
            >
              <button
                v-for="tab in tabs"
                :key="tab.name"
                type="button"
                role="tab"
                :class="['home-tab-item', { active: activeTab == tab.name }]"
                :aria-selected="activeTab == tab.name"
                @click="handleTabClick(tab.name)"
              >
                <span>{{ tab.title }}</span>
              </button>
            </nav>

            <!-- 商品展示方式 -->
            <div
              v-if="activeTab == 0"
              class="mobile-view-switch"
              role="group"
              :aria-label="$t('mobile.home.viewMode')"
            >
              <!-- 双列模式 -->
              <button
                type="button"
                :aria-label="$t('mobile.home.twoColumns')"
                :class="{ active: viewMode === 'waterfall' }"
                :aria-pressed="viewMode === 'waterfall'"
                @click="viewMode = 'waterfall'"
              >
                <van-icon name="apps-o" />
              </button>
              <!-- 列表模式 -->
              <button
                type="button"
                :aria-label="$t('mobile.home.listMode')"
                :class="{ active: viewMode === 'list' }"
                :aria-pressed="viewMode === 'list'"
                @click="viewMode = 'list'"
              >
                <van-icon name="bars" />
              </button>
            </div>
          </div>

          <!-- 我们是有底线的 -->
          <!-- 获取数据失败，请重新加载 -->
          <van-list
            v-show="activeTab == 0"
            :class="[
              'goods-waterfall-list',
              {
                'goods-waterfall-list--standalone': !hasClueViewPermission,
              },
            ]"
            v-model="loading"
            :finished="finished"
            :finished-text="$t('mobile.home.listEnd')"
            :error.sync="error"
            :error-text="$t('mobile.home.loadFailedReload')"
            @load="onLoad"
          >
            <div v-if="loading && list.length === 0" class="goods-waterfall">
              <div
                v-for="columnIndex in 2"
                :key="columnIndex"
                class="goods-waterfall__column"
              >
                <div
                  v-for="cardIndex in 2"
                  :key="cardIndex"
                  class="goods-card-skeleton"
                  aria-hidden="true"
                >
                  <div class="goods-card-skeleton__image"></div>
                  <div
                    class="goods-card-skeleton__line goods-card-skeleton__line--title"
                  ></div>
                  <div
                    class="goods-card-skeleton__line goods-card-skeleton__line--short"
                  ></div>
                </div>
              </div>
            </div>
            <div
              v-else-if="list.length && viewMode === 'waterfall'"
              class="goods-waterfall"
            >
              <div
                v-for="(column, columnIndex) in waterfallColumns"
                :key="columnIndex"
                class="goods-waterfall__column"
              >
                <GoodsItem
                  v-for="(item, itemIndex) in column"
                  :key="item.goodsCode || item.id || itemIndex"
                  :item="item"
                  :show-part-number="true"
                  publisher-chat
                  @publisher-chat="openPublisherChat($event, 'goods')"
                />
              </div>
            </div>
            <div v-else-if="list.length" class="goods-compact-list">
              <HomeGoodsListItem
                v-for="(item, index) in list"
                :key="item.goodsCode || item.id || index"
                :item="item"
                @select="onJump"
                publisher-chat
                @publisher-chat="openPublisherChat($event, 'goods')"
              />
            </div>
            <!-- 暂无可展示的商品 -->
            <van-empty
              v-else-if="!loading"
              class="goods-waterfall-empty"
              image="search"
              :description="$t('mobile.home.noProducts')"
            />
          </van-list>

          <div
            v-if="activeTab != 0 && hasClueViewPermission"
            class="mobile-clue-section"
          >
            <div class="mobile-clue-toolbar">
              <form
                class="mobile-clue-search"
                role="search"
                @submit.prevent="sekectList"
              >
                <!-- 筛选供需信息，已选{count}项 -->
                <!-- 筛选供需信息 -->
                <button
                  type="button"
                  :class="[
                    'mobile-clue-search__filter',
                    { active: advancedFilterCount > 0 },
                  ]"
                  :aria-label="
                    advancedFilterCount
                      ? $t('mobile.home.filterSelectedCount', {
                          count: advancedFilterCount,
                        })
                      : $t('mobile.home.filterClues')
                  "
                  @click="showFilter = !showFilter"
                >
                  <van-icon name="filter-o" />
                  <sup v-if="advancedFilterCount">{{
                    advancedFilterCount
                  }}</sup>
                </button>
                <!-- 搜索品牌、料号、类型或供应商 -->
                <input
                  v-model="infoFilters.model"
                  type="search"
                  inputmode="search"
                  enterkeyhint="search"
                  :placeholder="$t('mobile.home.clueSearchPlaceholder')"
                />
                <!-- 清除搜索关键词 -->
                <button
                  v-if="infoFilters.model"
                  type="button"
                  class="mobile-clue-search__clear"
                  :aria-label="$t('mobile.home.clearSearch')"
                  @click="clearInfoKeyword"
                >
                  <van-icon name="cross" />
                </button>
                <!-- 搜索供需信息 -->
                <button
                  type="submit"
                  class="mobile-clue-search__submit"
                  :aria-label="$t('mobile.home.searchClues')"
                >
                  <van-icon name="search" />
                </button>
              </form>
              <button
                v-if="activeTab == 2 && hasClueViewPermission"
                type="button"
                class="mobile-clue-import"
                @click="showImportDialog = true"
              >
                <van-icon name="plus" />
                <!-- 导入 -->
                <span>{{ $t("mobile.home.import") }}</span>
              </button>
            </div>

            <!-- 筛选供需信息 -->
            <div
              v-if="showFilter"
              class="mobile-filter-popover"
              role="dialog"
              aria-modal="false"
              :aria-label="$t('mobile.home.filterClues')"
              @click.stop
            >
              <div class="mobile-filter-popover__heading">
                <!-- 筛选 -->
                <strong>{{ $t("mobile.home.filter") }}</strong>
                <!-- 关闭筛选 -->
                <button
                  type="button"
                  :aria-label="$t('mobile.home.closeFilter')"
                  @click="showFilter = false"
                >
                  <van-icon name="cross" />
                </button>
              </div>

              <div class="mobile-filter-popover__content">
                <section class="mobile-filter-popover__section">
                  <h3>{{ $t("pcClue.type") }}</h3>
                  <div class="mobile-filter-option-grid">
                    <button v-for="option in clueTypeOptions" :key="`clue-type-${option.value}`" type="button"
                      :class="{ active: infoFilters.clueType === option.value }"
                      @click="infoFilters.clueType = option.value">{{ option.text }}</button>
                  </div>
                </section>
                <section class="mobile-filter-popover__section">
                  <!-- 货源地 -->
                  <h3>{{ $t("mobile.home.sourceRegion") }}</h3>
                  <div class="mobile-filter-option-grid">
                    <button
                      v-for="option in typeOptions"
                      :key="`type-${option.value}`"
                      type="button"
                      :class="{
                        active:
                          String(infoFilters.type) === String(option.value),
                      }"
                      @click="infoFilters.type = option.value"
                    >
                      {{ option.name }}
                    </button>
                  </div>
                </section>

                <section class="mobile-filter-popover__section">
                  <!-- 品牌 -->
                  <h3>{{ $t("mobile.home.brand") }}</h3>
                  <div class="mobile-filter-option-grid">
                    <button
                      v-for="option in mobileBrandOptions"
                      :key="`brand-${option.value}`"
                      type="button"
                      :class="{
                        active:
                          String(infoFilters.brand) === String(option.value),
                      }"
                      @click="infoFilters.brand = option.value"
                    >
                      {{ option.name }}
                    </button>
                    <!-- 收起 -->
                    <!-- 展开 -->
                    <button
                      v-if="brandList.length > 12"
                      type="button"
                      class="mobile-filter-option-more"
                      :aria-expanded="brandExpanded"
                      @click="brandExpanded = !brandExpanded"
                    >
                      {{
                        brandExpanded
                          ? $t("mobile.home.collapse")
                          : $t("mobile.home.expand")
                      }}
                    </button>
                  </div>
                </section>
              </div>

              <div class="mobile-filter-popover__actions">
                <!-- 重置 -->
                <button type="button" @click="resetFilters">
                  {{ $t("mobile.home.reset") }}
                </button>
                <!-- 查看结果 -->
                <button type="button" class="primary" @click="sekectList">
                  {{ $t("mobile.home.viewResults") }}
                </button>
              </div>
            </div>

            <div v-if="activeFilterLabels.length" class="mobile-filter-summary">
              <span v-for="label in activeFilterLabels" :key="label">
                {{ label }}
              </span>
              <!-- 清除 -->
              <button type="button" @click="resetFilters">
                {{ $t("mobile.home.clear") }}
              </button>
            </div>

            <!-- 我们是有底线的 -->
            <!-- 获取数据失败，请重新加载 -->
            <van-list
              v-model="infoLoading"
              :finished="infoFinished"
              :error.sync="infoError"
              :finished-text="$t('mobile.home.listEnd')"
              :error-text="$t('mobile.home.loadFailedReload')"
              @load="loadMoreInfo"
            >
              <div
                v-if="infoError && infoList.length === 0"
                class="info-state-panel"
              >
                <!-- 获取数据失败，请重试 -->
                <van-empty
                  image="error"
                  :description="$t('mobile.home.loadFailedRetry')"
                />
                <!-- 重新加载 -->
                <van-button type="primary" size="small" @click="retryInfoList">
                  {{ $t("mobile.home.reload") }}
                </van-button>
              </div>
              <div v-else class="info-list">
                <article
                  v-for="(item, index) in infoList"
                  :key="item.id || index"
                  class="info-item"
                  @click="handleInfoItemClick(item)"
                >
                  <header class="info-header">
                    <div class="info-header-content">
                      <strong class="info-brand">{{
                        item.partNumber || "--"
                      }}</strong>
                      <span v-if="item.goodsBrand">{{ item.goodsBrand }}</span>
                    </div>
                    <div class="info-checkbox" @click.stop>
                      <van-checkbox
                        :disabled="
                          item.supplierId !== 1 && item.supplierId !== 2
                        "
                        v-model="item.selected"
                        shape="square"
                        icon-size="20px"
                        @change="handleCustomSelect(item)"
                      />
                    </div>
                  </header>
                  <div class="info-body">
                    <div class="info-row">
                      <span class="info-label">{{ $t("pcClue.type") }}</span>
                      <span class="info-value">{{ Number(item.clueType) === 1 ? $t('pcHome.shortcut.supply') : Number(item.clueType) === 2 ? $t('pcHome.shortcut.demand') : '--' }}</span>
                    </div>
                    <div class="info-row">
                      <!-- 类型 -->
                      <span class="info-label">{{
                        $t("pcClue.category")
                      }}</span
                      ><span class="info-value">{{
                        item.productType || "--"
                      }}</span>
                    </div>
                    <div class="info-row">
                      <!-- 容量 -->
                      <span class="info-label">{{
                        $t("mobile.home.capacity")
                      }}</span
                      ><span class="info-value">{{
                        item.capacity || "--"
                      }}</span>
                    </div>
                    <div class="info-row">
                      <!-- 架构 -->
                      <span class="info-label">{{
                        $t("mobile.home.architecture")
                      }}</span
                      ><span class="info-value">{{
                        item.architecture || "--"
                      }}</span>
                    </div>
                    <div class="info-row">
                      <!-- 速率 -->
                      <span class="info-label">{{
                        $t("mobile.home.speed")
                      }}</span
                      ><span class="info-value">{{ item.speed || "--" }}</span>
                    </div>
                    <div class="info-row">
                      <!-- 球位 -->
                      <span class="info-label">{{
                        $t("mobile.home.ballCount")
                      }}</span
                      ><span class="info-value">{{ item.ball || "--" }}</span>
                    </div>
                    <div class="info-row">
                      <!-- 数量 -->
                      <span class="info-label">{{
                        $t("mobile.home.quantity")
                      }}</span
                      ><span class="info-value">{{
                        item.goodsQuantity || "--"
                      }}</span>
                    </div>
                  </div>
                  <div class="info-trade-row">
                    <!-- 单价 -->
                    <!-- 价格面议 -->
                    <span
                      ><small>{{ $t("mobile.home.unitPrice") }}</small
                      ><strong>{{
                        $formatPrice(item.price) ||
                        $t("mobile.home.priceOnRequest")
                      }}</strong></span
                    >
                    <!-- 批次 -->
                    <span
                      ><small>{{ $t("mobile.home.batch") }}</small
                      >{{ item.manufactureDate || "--" }}</span
                    >
                  </div>
                  <footer class="info-footer">
                    <!-- 货所在地 -->
                    <span v-if="item.deliveryAddress"
                      ><small>{{ $t("mobile.home.stockLocation") }}</small
                      >{{ item.deliveryAddress }}</span
                    >
                    <!-- 供应商 -->
                    <span v-if="item.supplierName"
                      ><small>{{ $t("mobile.home.supplier") }}</small
                      >{{ item.supplierName }}</span
                    >
                    <!-- 发布时间 -->
                    <span v-if="item.createTime"
                      ><small>{{ $t("mobile.home.publishedAt") }}</small
                      >{{ item.createTime }}</span
                    >
                  </footer>
                  <div class="info-publisher-chat">
                    <PublisherChatAvatar :name="item.publisherDisplayName" :avatar="item.publisherAvatar" :user-id="item.publisherUserId" :available="item.publisherAvailable" :busy="publisherOpeningId === String(item.id)" @chat="openPublisherChat(item, 'clue')" />
                  </div>
                </article>
              </div>
              <!-- 暂无信息 -->
              <van-empty
                v-if="!infoLoading && !infoError && infoList.length <= 0"
                :description="$t('mobile.home.noClues')"
              />
            </van-list>

            <div
              v-if="selectedInfoItems.length > 0"
              class="selected-action-bar"
            >
              <div class="selected-action-bar-text">
                <strong>{{ selectedInfoItems.length }}</strong>
                <!-- 项已选择 -->
                <span>{{ $t("mobile.home.itemsSelected") }}</span>
              </div>
              <div class="selected-action-bar__buttons">
                <!-- 删除 -->
                <van-button plain type="danger" @click="handleDeleteSelected">
                  {{ $t("mobile.home.delete") }}
                </van-button>
                <!-- 审核 -->
                <van-button
                  v-if="isAudit"
                  type="primary"
                  @click="handleAuditSelected"
                >
                  {{ $t("mobile.home.review") }}
                </van-button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { Dialog, Toast } from "vant";
import { mapState } from "vuex";
import { shortcutLabelKeys } from "@/i18n/home/index.js";
import { navigationMessageKey } from "@/i18n/catalog.js";
import GoodsItem from "@/components/GoodsItem.vue";
import HomeGoodsListItem from "@/components/HomeGoodsListItem.vue";
import PublisherChatAvatar from '@/components/chat-workbench/PublisherChatAvatar.vue';
import { openSupplyDemandConsultation } from '@/api/agentService';
import { resolveAgentServiceCurrentUserId } from '@/services/agent-service-state';
import SearchBox from "@/components/SearchBox.vue";
import {
  getHomeNav,
  getHomeGoods,
  getBannerAdv,
  getHomeData,
  getHomeClue,
  getClueCapabilities,
} from "@/api/home";
import { getUserInfoDetail } from "@/api/user.js";
import { listPendingClue, swPendingFollowList } from "@/api/bizOppor";
import { updateFile } from "@/api/upload";
import { importClue, auditClue, reviewInformation, deleteClue } from "@/api/clue";
import { getBrandSearch } from "@/api/picker";
import { recordEvent } from "@/utils/parameterMonitor";
import { resolveHomeDoodle } from "@/indexlogo/schedule";
export default {
  name: "HomePage",
  components: {
    GoodsItem,
    HomeGoodsListItem,
    PublisherChatAvatar,
    SearchBox,
  },
  data() {
    return {
      bannerList: [], //轮播
      logoList: [{}, {}], //logo图--要初始化因为我上面直接用了
      activeMobileDoodle: null,
      mobileDoodleTimer: null,
      navList: [], //导航
      brandShowcaseList: [
        {
          name: "三星",
          logo: require("./assets/brands/samsung.png"),
        },
        {
          name: "西部数据",
          logo: require("./assets/brands/western-digital.png"),
        },
        {
          name: "慧荣科技",
          logo: require("./assets/brands/silicon-motion.png"),
        },
        {
          name: "忆恒创源",
          logo: require("./assets/brands/starblaze.png"),
        },
        {
          name: "联芸科技",
          logo: require("./assets/brands/maxio.png"),
        },
        {
          name: "铠侠",
          logo: require("./assets/brands/kioxia.png"),
        },
        {
          name: "佰维存储",
          logo: require("./assets/brands/biwin.png"),
        },
        {
          name: "江波龙",
          logo: require("./assets/brands/longsys.png"),
        },
        {
          name: "海力士",
          logo: require("./assets/brands/sk-hynix.png"),
        },
        {
          name: "希捷",
          logo: require("./assets/brands/seagate.png"),
        },
        {
          name: "长江存储",
          logo: require("./assets/brands/ymtc.jpg"),
        },
        {
          name: "英韧科技",
          logo: require("./assets/brands/innogrit.jpg"),
        },
      ],
      canScrollBrandsRight: false,
      proList: [], //商品
      advImg: null,
      params: {
        pageSize: 20,
        pageNum: 0,
        pageNum: 1,
        which: 2,
        position: "HOME",
        status: 0,
        goodsType: null,
      },

      list: [],
      homeGoodsRequestId: 0,
      active: 0,
      serachShow: false, //搜索查询状态刷新状态
      checkMore: true,
      refreshing: false, //刷新状态默认值
      loading: false,
      finished: false,
      error: false,
      isFixed: false, //搜索框是否固定顶部
      scrollContainer: null,
      hasNewClue: false, //是否有新订单
      userBusinessType: null, // 用户业务类型
      activeTab: this.$route.query.tab === "information" ? 2 : this.$route.query.tab === "supply-demand" ? 1 : 0, // 当前标签页
      viewMode: "waterfall", // waterfall(双列) / list(字段列表)
      // 信息列表数据
      infoList: [],
      publisherOpeningId: '',
      publisherChatEpoch: 0,
      infoRequestVersion: 0,
      infoLoading: false,
      infoFinished: false,
      infoError: false,
      // 信息列表筛选条件
      infoFilters: {
        brand: "",
        model: "",
        type: "",
        supplier: "",
        productType: "",
        pageNum: 1,
        pageSize: 10,
        clueType: 0,
      },
      brandList: [],
      brandExpanded: false,
      // 选中的信息列表项
      selectedInfoItems: [],
      // 批量上传相关
      showImportDialog: false,
      importFileList: [],
      isImportSubmitting: false,
      isAudit: false,
      // 查询区域展开状态
      showFilter: false,
    };
  },
  watch: {
    catalogIdentity() {
      this.publisherChatEpoch++;
      this.publisherOpeningId = '';
      this.list = [];
      this.queryList();
    },
    hasClueViewPermission(allowed) {
      if (!allowed) {
        this.infoRequestVersion += 1;
        this.infoList = [];
        this.isAudit = false;
        this.activeTab = 0;
      }
    },
  },
  computed: {
    clueTypeOptions() {
      return [
        { value: 0, text: this.$t("pcHome.all") },
        { value: 1, text: this.$t("pcHome.shortcut.supply") },
        { value: 2, text: this.$t("pcHome.shortcut.demand") },
      ];
    },
    catalogIdentity() { return this.$store.getters.token || ""; },
    tabList() {
      return [
        // 供应
        { title: this.$t("pcHome.shortcut.supply"), type: 1 },
        // 需求
        { title: this.$t("pcHome.shortcut.demand"), type: 2 },
      ];
    },

    // ...mapState({
    //   userInfo: (state) => state.user.userDetails,
    // }),
    mobileFeaturedLogoSrc() {
      return (
        this.activeMobileDoodle?.image ||
        this.logoList[0]?.image ||
        require("@/assets/pc/home-search-logo.png")
      );
    },
    mobileFeaturedLogoAlt() {
      return this.activeMobileDoodle
        ? `先搜${this.activeMobileDoodle.name}节日标志`
        : this.logoList[0]?.title || "先搜";
    },
    searchBoxClass() {
      return {
        fixed: this.isFixed,
      };
    },
    waterfallColumns() {
      const columns = [[], []];
      const estimatedHeights = [0, 0];

      this.list.forEach((item) => {
        const estimatedCardHeight = 260 + (item.shopContacts ? 27 : 0);
        const targetColumn = estimatedHeights[0] <= estimatedHeights[1] ? 0 : 1;

        columns[targetColumn].push(item);
        estimatedHeights[targetColumn] += estimatedCardHeight + 10;
      });

      return columns;
    },
    typeOptions() {
      return [
        // 全部
        { name: this.$t("mobile.home.all"), value: "" },
        // 大陆
        { name: this.$t("mobile.home.mainland"), value: "1" },
        // 港澳台
        { name: this.$t("mobile.home.hongKongMacaoTaiwan"), value: "2" },
      ];
    },
    brandOptions() {
      return [
        // 全部品牌
        { name: this.$t("mobile.home.allBrands"), value: "" },
        ...this.brandList.map((item) => ({
          name: item.text,
          value: item.text,
        })),
      ];
    },
    mobileBrandOptions() {
      return [
        // 全部品牌
        { name: this.$t("mobile.home.allBrands"), value: "" },
        ...(this.brandExpanded
          ? this.brandOptions.slice(1)
          : this.brandOptions.slice(1, 13)),
      ];
    },
    selectedBrandText() {
      const selected = this.brandOptions.find(
        (item) => String(item.value) === String(this.infoFilters.brand)
      );
      // 全部品牌
      return selected ? selected.name : this.$t("mobile.home.allBrands");
    },
    activeFilterLabels() {
      const labels = [];
      if (this.infoFilters.clueType) labels.push(`${this.$t("pcClue.type")}: ${this.clueTypeOptions.find(option => option.value === this.infoFilters.clueType).text}`);
      if (this.infoFilters.brand !== "") {
        labels.push(
          // 品牌
          `${this.$t("mobile.home.brand")}: ${this.selectedBrandText}`
        );
      }
      if (this.infoFilters.type !== "") {
        labels.push(
          // 货源地
          `${this.$t("mobile.home.sourceRegion")}: ${this.getTypeText(
            this.infoFilters.type
          )}`
        );
      }
      if (this.infoFilters.model.trim()) {
        labels.push(
          // 关键词
          `${this.$t("mobile.home.keyword")}: ${this.infoFilters.model.trim()}`
        );
      }
      return labels;
    },
    advancedFilterCount() {
      return [this.infoFilters.brand, this.infoFilters.type, this.infoFilters.clueType].filter(
        (value) => value !== "" && value !== 0
      ).length;
    },
    hasClueViewPermission() {
      return Boolean(this.$store.getters.token) && String(this.userBusinessType || "").split(",").some(role => role.trim() === "10");
    },
    // 标签页配置
    tabs() {
      const basicTabs = [
        // 成交信息
        { title: this.$t("mobile.home.tradeInfo"), name: "0" },
      ];
      // 谈判人角色显示信息标签
      if (this.hasClueViewPermission) {
        // 供需信息
        basicTabs.push({ title: this.$t("mobile.home.clueInfo"), name: "1" });
      }
      if (this.hasClueViewPermission) basicTabs.splice(1, 0, { title: this.$t("mobile.home.information"), name: "2" });
      return basicTabs;
    },
  },
  mounted() {
    // 找到滚动容器（这里假设是body或.common_container容器）
    this.scrollContainer =
      this.$refs.homeRef || document.querySelector(".home");
    // 使用Vue的事件监听方式
    if (this.scrollContainer) {
      this.scrollContainer.addEventListener(
        "scroll",
        this.updateSearchBoxPosition
      );
      // 初始化搜索框位置
      this.updateSearchBoxPosition();
    }
    this.loadBrandList();
    this.updateBrandScrollState();
    window.addEventListener("resize", this.updateBrandScrollState);
    this.mobileDoodleTimer = window.setInterval(
      this.refreshMobileDoodle,
      10000
    );
  },
  beforeDestroy() {
    this.publisherChatEpoch++;
    // 移除滚动事件监听
    if (this.scrollContainer) {
      console.log("移除滚动事件监听");
      this.scrollContainer.removeEventListener(
        "scroll",
        this.updateSearchBoxPosition
      );
    }
    window.removeEventListener("resize", this.updateBrandScrollState);
    window.clearInterval(this.mobileDoodleTimer);
  },
  methods: {
    /** 沿用PC的业务咨询协议，商品/线索的接待人和可见性由Spring重新核验。 */
    async openPublisherChat(item, kind) {
      const user = this.$store.state.user || {};
      const userId = resolveAgentServiceCurrentUserId(user);
      const token = String(user.userInfo?.token || '');
      if (!userId || !token) { window.dispatchEvent(new Event('open-login-dialog')); return; }
      const epoch = ++this.publisherChatEpoch;
      this.publisherOpeningId = '';
      if (kind === 'goods') {
        if (item.goodsCode && item.shopContacts && String(item.shopContactsId || '') !== String(userId))
          this.$store.commit('chatWorkbench/open', { kind: 'PRODUCT', sceneType: 'PRODUCT', goodsCode: String(item.goodsCode) });
        return;
      }
      if (!item.publisherAvailable || !/^[1-9]\d*$/.test(String(item.publisherUserId || '')) || String(item.publisherUserId) === String(userId)) return;
      const route = this.$route.fullPath;
      this.publisherOpeningId = String(item.id);
      const current = () => epoch === this.publisherChatEpoch && route === this.$route.fullPath
        && token === String(this.$store.state.user?.userInfo?.token || '')
        && String(userId) === String(resolveAgentServiceCurrentUserId(this.$store.state.user || {}));
      try {
        const entryId = globalThis.crypto?.randomUUID?.() || `entry_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const response = await openSupplyDemandConsultation({ clueId: String(item.id), entryId });
        if (!current()) return;
        if (response.code !== 200 || !response.data?.conversation || !response.data?.context)
          throw new Error(response.msg || this.$t('pcHome.publisherConsultationFailed'));
        this.$store.commit('chatWorkbench/open', { kind: 'SUPPLY_DEMAND', session: response.data });
      } catch (error) {
        if (current()) Toast.fail(error.message || this.$t('pcHome.publisherConsultationFailed'));
      } finally {
        if (epoch === this.publisherChatEpoch) this.publisherOpeningId = '';
      }
    },
    displayShortcutTitle(item) {
      const key = shortcutLabelKeys[item.text] || navigationMessageKey(item.url, item.text);
      // 动态简中示例：按首页快捷入口和导航的受控映射显示；未知配置名称原样保留；完整中文见对应 zh-CN.js，示例不代表固定返回值
      // nav.home：首页
      // nav.publish：发布
      // nav.featured：精选
      // nav.bidding：竞标
      // nav.supply：供需
      // nav.wiki：百科
      return key ? this.$t(key) : item.text;
    },
    refreshMobileDoodle() {
      this.activeMobileDoodle = resolveHomeDoodle(
        new Date(),
        this.$route.query.doodle || ""
      );
    },
    isEnabledHomeNav(item) {
      return ["精选", "供需"].includes(String(item?.text || "").trim());
    },
    onHomeNavClick(item) {
      if (!this.isEnabledHomeNav(item)) return;
      this.onJump(item);
    },
    updateBrandScrollState() {
      this.$nextTick(() => {
        const viewport = this.$refs.brandViewport;
        if (!viewport) return;
        const maxScrollLeft = Math.max(
          0,
          viewport.scrollWidth - viewport.clientWidth
        );
        this.canScrollBrandsRight = viewport.scrollLeft < maxScrollLeft - 1;
      });
    },
    getBrandName(item) {
      // 品牌
      return item?.name || item?.text || this.$t("mobile.home.brand");
    },
    getBrandInitial(item) {
      return (
        this.getBrandName(item).trim().slice(0, 1).toUpperCase() ||
        // 品
        this.$t("mobile.home.brandInitial")
      );
    },
    openBrand(item) {
      this.onProductSearch(this.getBrandName(item));
    },
    // 处理商品搜索（子组件触发的事件）
    onProductSearch(value) {
      const question = (value || "").trim();
      if (!question) return;

      // 移动端首页搜索和 PC 全站搜索保持同一条商品检索链路，不再跳 AI 对话页。
      this.$router.push({
        path: "/search",
        query: { keyword: question },
      });
    },
    isLogin() {
      return this.$store.getters.token;
    },
    async getUserInfoDetail({ showTip = true } = {}) {
      const response = await getUserInfoDetail({ skipAuthRedirect: true, skipErrorMessage: true });
      if (response.code === 200) {
        const { businessType } = response.data.userInfo;
        this.userBusinessType = businessType; // 保存用户业务类型
        if (!this.hasClueViewPermission && Number(this.activeTab) !== 0) this.activeTab = 0;
        this.viewMode = businessType == 10 ? "list" : "waterfall";
        // 谈判人角色|| 商务角色
        if (showTip && (businessType == 10 || businessType == 20)) {
          this.needShowTip(businessType);
        }
        // 如果是谈判人，获取信息列表
        if (this.hasClueViewPermission) {
          this.getInfoList();
        }
      }
    },
    async needShowTip(type) {
      // 确保类型一致，避免 switch (===) 匹配失败
      let businessType = Number(type);
      switch (businessType) {
        case 10:
          // 谈判人-新订单列表
          this.hasNewClue = await listPendingClue();
          break;
        case 20:
          // 商务-新订单列表
          this.hasNewClue = await swPendingFollowList();
          break;
      }
      if (this.hasNewClue.rows.length) {
        // 把已经提醒过的状态保存起来，下次进来就不提醒了
        sessionStorage.setItem("hasTip", 1);
        const path =
          businessType == 10
            ? "/follow/pendingFollow"
            : "/follow/swPendingFollow";
        Dialog.confirm({
          // 您有新订单!
          title: this.$t("mobile.home.newOrder"),
          // 去接单
          confirmButtonText: this.$t("mobile.home.acceptOrder"),
          confirmButtonColor: "#4689F2",
        })
          .then(() => {
            this.$router.push({
              path,
            });
          })
          .catch(() => {
            // on cancel
          });
      }
    },

    onJump(item) {
      if (item.text === "供需") {
        if (!this.hasClueViewPermission) { this.$router.push("/featured"); return; }
        this.handleTabClick(2);
        this.$router.push({ path: "/featured", query: { tab: "information" } }).catch(error => {
          if (error.name !== "NavigationDuplicated") throw error;
        });
        return;
      }
      if (item.path) {
        if (item.text == "供应" || item.text == "需求") {
          this.$router.push({
            path: item.path,
            query: { active: item.text == "供应" ? 0 : 1 },
          });
        } else {
          this.$router.push(item.path);
        }
      } else if (item.goodsCode) {
        this.$router.push(`/prodetail/${item.goodsCode}`);
      }
    },
    // 标签页切换
    handleTabClick(tabName) {
      if (Number(tabName) !== 0 && !this.hasClueViewPermission) tabName = 0;
      this.activeTab = tabName;
      if (this.activeTab == 0) {
        if (this.list.length == 0) {
          this.onLoad();
        }
      } else {
        // 切换到信息标签，确保数据已加载
        this.infoList = [];
        this.infoFilters.pageNum = 1;
        this.selectedInfoItems = [];
        this.getInfoList();
      }
    },
    // 获取信息列表数据
    async getInfoList(isLoadMore = false) {
      // 只有谈判人角色才请求信息列表
      if (this.hasClueViewPermission) {
        const information = this.activeTab == 2;
        const version = ++this.infoRequestVersion;
        if (!isLoadMore) {
          this.infoLoading = true;
          this.infoError = false;
          this.infoFinished = false;
        }
        this.isAudit = false;
        try {
          try {
            const capability = await getClueCapabilities();
            if (version !== this.infoRequestVersion) return;
            this.isAudit = capability?.code === 200 && (information ? capability.data?.canReviewInformation : capability.data?.canReview) === true;
          } catch {
            if (version !== this.infoRequestVersion) return;
          }
          const response = await getHomeClue({ ...this.infoFilters, clueStatus: information ? 0 : 1 });
          if (version !== this.infoRequestVersion || information !== (this.activeTab == 2)) return;
          if (response.code === 200) {
            const processedData = response.rows.map((item) => {
              // 审核能力由服务端读取，不从供应商展示字段推断。
              let formattedCreateTime = "";
              if (item.createTime) {
                const date = new Date(item.createTime);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                const hours = String(date.getHours()).padStart(2, "0");
                const minutes = String(date.getMinutes()).padStart(2, "0");
                const seconds = String(date.getSeconds()).padStart(2, "0");
                formattedCreateTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
              }
              return {
                id: item.id || item.clueId || "",
                clueType: Number(item.clueType),
                partNumber: item.partNumber || "",
                goodsBrand: item.goodsBrand || "",
                productType: item.productType || "",
                capacity: item.capacity || "",
                architecture: item.architecture || "",
                speed: item.speed || "",
                ball: item.ball || "",
                price:
                  item.goodsPrice !== null && item.goodsPrice !== undefined
                    ? `${item.goodsPrice}${
                        item.goodsPriceUnit === 0
                          ? "CNY"
                          : item.goodsPriceUnit === 10
                          ? "USD"
                          : ""
                      }`
                    : "",
                goodsQuantity: item.goodsQuantity || "",
                manufactureDate: item.manufactureDate || "",
                deliveryAddress: item.deliveryAddress || "",
                supplierName: item.supplierName || "",
                supplierId: item.supplierId || "",
                publisherUserId: String(item.publisherUserId || ''),
                publisherDisplayName: item.publisherDisplayName || '',
                publisherAvatar: item.publisherAvatar || '',
                publisherAvailable: item.publisherAvailable === true,
                selected: false,
                createTime: formattedCreateTime,
              };
            });
            if (isLoadMore) {
              this.infoList = this.infoList.concat(processedData);
            } else {
              this.infoList = processedData;
            }
            if (processedData.length < this.infoFilters.pageSize) {
              this.infoFinished = true;
            }
          } else {
            this.infoError = true;
          }
        } catch (error) {
          console.error("获取信息列表失败:", error);
          if (version === this.infoRequestVersion) this.infoError = true;
        } finally {
          if (version === this.infoRequestVersion) this.infoLoading = false;
        }
      }
    },
    // 加载更多信息
    loadMoreInfo() {
      if (this.infoFinished) return;
      this.infoLoading = true;
      setTimeout(() => {
        this.infoFilters.pageNum += 1;
        this.getInfoList(true);
      }, 500);
    },
    retryInfoList() {
      this.infoFilters.pageNum = 1;
      this.infoList = [];
      this.infoError = false;
      this.getInfoList();
    },
    // 点击信息项
    async handleInfoItemClick(item) {
      const tab = this.activeTab == 2 ? "information" : "supply-demand";
      await this.$router.replace({ path: this.$route.path, query: { ...this.$route.query, tab } }).catch(() => {});
      this.$router.push({
        path: "/clue/clueDetail",
        query: { id: item.id, tab },
      });
    },
    // 重置筛选条件
    resetFilters() {
      this.infoFilters = {
        brand: "",
        model: "",
        type: "",
        supplier: "",
        productType: "",
        pageNum: 1,
        pageSize: 10,
        clueType: 0,
      };
      this.clearInfoSelection();
      this.infoList = [];
      this.showFilter = false;
      this.getInfoList();
    },
    clearInfoKeyword() {
      this.infoFilters.model = "";
      this.sekectList();
    },
    // 查询方法
    sekectList() {
      this.clearInfoSelection();
      this.infoList = [];
      this.infoFilters.pageNum = 1;
      this.showFilter = false;
      this.getInfoList();
    },
    clearInfoSelection() {
      this.infoList.forEach((item) => {
        item.selected = false;
      });
      this.selectedInfoItems = [];
      this.isAudit = false;
    },
    async loadBrandList() {
      try {
        const response = await getBrandSearch({ name: "" });
        this.brandList =
          response?.code == 200 && Array.isArray(response.data)
            ? response.data
            : [];
      } catch (error) {
        this.brandList = [];
        console.error("移动端品牌筛选加载失败", error);
      }
    },
    // 获取货源地显示文本
    getTypeText(value) {
      const type = this.typeOptions.find((t) => t.value === value);
      return type ? type.name : "";
    },
    // 处理文件上传
    async handleFileUpload(file) {
      file.status = "uploading";
      // 上传中...
      file.message = this.$t("mobile.home.uploading");
      const formData = new FormData();
      formData.append("file", file.file);
      try {
        const resp = await updateFile(formData);
        if (resp.code == 200) {
          file.status = "success";
          // 上传成功
          file.message = this.$t("mobile.home.uploadSucceeded");
          file.url = resp.url;
        } else {
          // 上传失败
          Toast.fail(this.$t("mobile.home.uploadFailed"));
          file.status = "failed";
          // 上传失败
          file.message = this.$t("mobile.home.uploadFailed");
        }
      } catch (err) {
        // 上传失败
        Toast.fail(this.$t("mobile.home.uploadFailed"));
        file.status = "failed";
        // 上传失败
        file.message = this.$t("mobile.home.uploadFailed");
      }
    },
    // 删除文件
    beforeDeleteFile() {
      return true;
    },
    // 提交导入
    async submitImport() {
      if (this.importFileList.length === 0) {
        // 请选择要上传的文件
        Toast.warning(this.$t("mobile.home.selectUploadFile"));
        return;
      }

      const fileItem = this.importFileList[0];
      if (fileItem.status !== "success") {
        // 文件上传中，请等待上传完成
        Toast.warning(this.$t("mobile.home.waitForUpload"));
        return;
      }

      const formData = new FormData();
      formData.append("file", fileItem.file);
      this.isImportSubmitting = true;
      try {
        const res = await importClue(formData);
        if (res.code == 200) {
          // 导入成功
          Toast.success(this.$t("mobile.home.importSucceeded"));
          this.showImportDialog = false;
          this.importFileList = [];
          this.activeTab = 2;
          this.sekectList();
        } else {
          // 导入失败
          Toast.fail(res.msg || this.$t("mobile.home.importFailed"));
        }
      } catch (error) {
        console.error("供需信息导入失败", error);
        // 导入失败，请稍后重试
        Toast.fail(this.$t("mobile.home.importFailedRetry"));
      } finally {
        this.isImportSubmitting = false;
      }
    },
    // 处理取消操作
    handleCancel() {
      this.showImportDialog = false;
      this.importFileList = [];
    },
    handleCustomSelect(row) {
      console.log(row, "row");
      if (row.selected) {
        const exists = this.selectedInfoItems.find(
          (item) => item.id === row.id
        );
        console.log(exists, "exists");
        if (!exists) {
          this.selectedInfoItems.push(row);
        }
        console.log(this.selectedInfoItems, "this.selectedInfoItems");
      } else {
        this.selectedInfoItems = this.selectedInfoItems.filter(
          (item) => item.id !== row.id
        );
      }
    },
    // 处理删除选中项
    handleDeleteSelected() {
      const ids = this.selectedInfoItems.map((item) => item.id);
      if (ids.length === 0) return;

      Dialog.confirm({
        // 删除确认
        title: this.$t("mobile.home.confirmDeletion"),
        // 是否删除这{count}条数据？
        message: this.$t("mobile.home.deleteSelectedConfirm", {
          count: this.selectedInfoItems.length,
        }),
        // 确定
        confirmButtonText: this.$t("mobile.home.confirm"),
        // 取消
        cancelButtonText: this.$t("mobile.home.cancel"),
      })
        .then(() => {
          deleteClue({ ids })
            .then((resp) => {
              if (resp.code === 200) {
                // 删除成功
                Toast.success(this.$t("mobile.home.deleteSucceeded"));
                this.infoList.forEach((item) => {
                  item.selected = false;
                });
                this.selectedInfoItems = [];
                this.getInfoList();
              }
            })
            .catch((error) => {
              console.error("删除失败:", error);
              // 删除失败，请稍后重试
              Toast.fail(this.$t("mobile.home.deleteFailedRetry"));
            });
        })
        .catch(() => {
          // 取消删除
        });
    },
    // 处理审核选中项
    handleAuditSelected() {
      const ids = this.selectedInfoItems.map((item) => item.id);
      const information = this.activeTab == 2;
      if (ids.length === 0) return;

      Dialog.confirm({
        // 审核确认
        title: this.$t("mobile.home.confirmReview"),
        // 是否审核这{count}条数据？
        message: this.$t("mobile.home.reviewSelectedConfirm", {
          count: this.selectedInfoItems.length,
        }),
        // 确定
        confirmButtonText: this.$t("mobile.home.confirm"),
        // 取消
        cancelButtonText: this.$t("mobile.home.cancel"),
      })
        .then(() => {
          if (information !== (this.activeTab == 2)) return;
          (information ? reviewInformation : auditClue)({ ids }).then((resp) => {
            if (resp.code === 200) {
              // 审核成功
              Toast.success(this.$t("mobile.home.reviewSucceeded"));
              this.infoList.forEach((item) => {
                item.selected = false;
              });
              this.selectedInfoItems = [];
              this.getInfoList();
              this.queryList();
            }
          });
        })
        .catch(() => {
          // 取消审核
        });
    },
    // 查询banner或者adv【type:1logo，2banner，3special，4adv】
    queryBannerAdv(params, callback) {
      getBannerAdv(params)
        .then((resp) => {
          callback(resp);
        })
        .catch((error) => {
          console.error("Error fetching banner data:", error);
        });
    },
    // 商品缺图时与 PC 首页保持一致：用品牌和料号生成正方形文字图片。
    drawTextCanvas(brand, name) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const width = (canvas.width = 600);
      const height = (canvas.height = 600);

      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      const textWidth = width * 0.7;
      const adjustFontSize = (text, maxWidth) => {
        let fontSize = 48;
        ctx.font = `${fontSize}px Arial`;
        while (ctx.measureText(text).width > maxWidth && fontSize > 12) {
          fontSize -= 2;
          ctx.font = `${fontSize}px Arial`;
        }
        return fontSize;
      };

      if (brand) {
        const brandFontSize = adjustFontSize(brand, textWidth);
        ctx.font = `${brandFontSize}px Arial`;
        ctx.fillText(brand, width / 2, height / 2 - 30);
      }
      if (name) {
        const nameFontSize = adjustFontSize(name, textWidth);
        ctx.font = `${nameFontSize}px Arial`;
        ctx.fillText(name, width / 2, height / 2 + 30);
      }

      return canvas.toDataURL("image/png");
    },
    // 查询商品列表接口
    // queryList() {
    //   getHomeGoods(this.params).then((res) => {
    //     this.loading = false;
    //     if (res.code == 200 && res.data) {
    //       if (res.data.length == 0) {
    //         this.finished = true;
    //       } else {
    //         this.finished = false;
    //         this.list = this.list.concat(res.data);
    //       }
    //     } else {
    //       this.error = true;
    //     }
    //   });
    // },
    queryList() {
      const identity = this.catalogIdentity;
      const requestId = ++this.homeGoodsRequestId;
      //1:pc 2:H5 现在集体用1，后续根据需求再修改
      const data = { ...this.params, which: 1 };
      getHomeGoods(data).then((res) => {
        if (requestId !== this.homeGoodsRequestId || identity !== this.catalogIdentity) return;
        this.loading = false;
        this.finished = true;
        if (res.code == 200 && res.data) {
          this.list = res.data ?? [];
          this.list.forEach((item) => {
            if (!item.goodsImage || item.goodsImage === "") {
              item.goodsImage = this.drawTextCanvas(
                item.goodsBrand || "",
                // 无料号
                item.goodsNum || this.$t("mobile.home.noPartNumber")
              );
            }
          });
        }
      });
    },
    //页面加载
    onLoad() {
      this.loading = false;
      // 模拟更新数据
      if (this.checkMore) {
        this.loading = true;
        // 如果有新数据
        setTimeout(() => {
          this.queryList();
        }, 300);
      } else {
        // 如果没有新数据
        this.finished = true;
      }
    },
    // onLoad() {
    //   this.loading = true;
    //   this.params.pageNum = this.params.pageNum + 1;
    //   this.queryList();
    // },

    // tabs分类
    // supeActive() {
    //   if (this.active == 0) {
    //     this.params.goodsType = 1;
    //   }
    //   if (this.active == 1) {
    //     this.params.goodsType = 2;
    //   }
    // },
    onTab(index) {
      this.active = index;
      this.params.pageNum = 1;
      this.serachShow = true;
      // this.supeActive();
      this.queryList();
    },
    // onTab(index) {
    //   this.active = index;
    //   this.serachShow = true;
    //   this.supeActive();
    //   this.onLoad();
    // },

    // 更新搜索框位置
    updateSearchBoxPosition() {
      if (!this.scrollContainer) return;

      const stickyThreshold = 177; // 品牌区 185px，搜索框接近顶部 8px 时吸顶
      const searchBoxHeight = 46;
      const { scrollTop, scrollHeight, clientHeight } = this.scrollContainer;
      const maxScrollTop = Math.max(0, scrollHeight - clientHeight);
      const hasVisibleItems =
        this.activeTab == 0 ? this.list.length > 0 : this.infoList.length > 0;

      // 空列表或吸顶后没有足够滚动空间时不启用 fixed，避免页面底部回弹
      // 反复跨过阈值，造成搜索框在 fixed/normal 状态之间持续跳动。
      const canFix =
        hasVisibleItems && maxScrollTop > stickyThreshold + searchBoxHeight;
      const isFixed = canFix && scrollTop > stickyThreshold;

      // 关闭滚动事件日志

      // 更新固定状态
      this.isFixed = isFixed;
      // 对于Vue 2，通过动态类名控制样式
    },
  },
  async created() {
    this.refreshMobileDoodle();
    // 登录状态判断
    const hasTiped = sessionStorage.getItem("hasTip");
    if (this.isLogin()) {
      this.getUserInfoDetail({ showTip: !hasTiped });
    }

    this.queryBannerAdv(
      { which: 2, type: 1, position: "HOME", status: 0 },
      (res) => {
        if (res.code == 200 && res.data && res.data.length > 0) {
          this.logoList = res.data.sort((a, b) => a.sort - b.sort);
        }
      }
    );
    this.queryBannerAdv(
      { which: 2, type: 2, position: "HOME", status: 0 },
      (res) => {
        if (res.code == 200 && res.data && res.data.length > 0) {
          this.bannerList = res.data;
        }
      }
    );
    this.queryBannerAdv(
      { which: 2, type: 4, position: "HOME", status: 0 },
      (res) => {
        if (res.code == 200 && res.data && res.data.length > 0) {
          this.advImg = res.data[0].image;
        }
      }
    );
    getHomeNav({
      pageId: 0,
      which: 2,
      position: "HOME",
      status: 0,
    }).then((resp) => {
      this.navList = resp.data;
    });
    // 初始化商品列表数据
    this.onLoad();
    // const {
    //   data: { pageData },
    // } = await getHomeData();
    // this.bannerList = pageData.items[1].data;
    // this.navList = pageData.items[3].data;
    // this.proList = pageData.items[6].data;
    // console.log(this.bannerList);
  },
};
</script>

<style lang="less" scoped>
.home {
  position: relative;
  height: 100vh;
  overflow-x: hidden;
  overflow-y: auto;
  color: #252b33;
  background: #ffffff;
  -webkit-overflow-scrolling: touch;
}

.main-container {
  position: relative;
  z-index: 1;
  display: flex;
  width: 100%;
  flex-direction: column;
  box-sizing: border-box;
  padding: 0 16px calc(@bottom-bar-height + 12px);
}

.home-upper {
  position: relative;

  .home-bg {
    position: absolute;
    top: 0;
    right: -16px;
    left: -16px;
    z-index: 0;
    height: var(--custom-bg-height);

    &::after {
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      height: 64px;
      pointer-events: none;
      background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.72) 58%,
        #ffffff 100%
      );
      content: "";
    }
  }

  &__content {
    position: relative;
    z-index: 1;
  }

  .home-brand-space {
    display: flex;
    width: 100%;
    height: 185px;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding-top: env(safe-area-inset-top);
  }

  .xsou-title {
    display: block;
    width: 70%;
    height: 80px;
    margin: 0;
    object-fit: contain;
  }

  .home-search-slot {
    width: 100%;
    height: 46px;
    flex: 0 0 46px;
  }

  ::v-deep .out-search-container {
    margin-top: 0;

    &.fixed {
      position: fixed;
      top: 8px;
      right: 16px;
      left: 16px;
      z-index: 999;
      max-width: 668px;
      margin: 0 auto;
      background: transparent;
    }
  }

  ::v-deep .search-container {
    box-sizing: border-box;
    height: 46px;
    padding: 0;
    margin: 0;
    border: 1px solid #e9edf3;
    border-radius: 22px;
    box-shadow: 0 2px 8px rgba(30, 56, 96, 0.04);

    &.needFixed {
      margin: 0;
      box-shadow: 0 4px 16px rgba(30, 56, 96, 0.12);
    }
  }

  ::v-deep .search-input {
    height: 100%;
    padding: 0 6px 0 14px;
    color: #344054;
    font-size: 12px;

    &::placeholder {
      color: #98a2b3;
    }
  }

  ::v-deep .search-btn {
    display: flex;
    width: 72px;
    height: 34px;
    align-items: center;
    justify-content: center;
    padding: 0;
    margin-right: 5px;
    border-radius: 18px;
  }

  .grid-container {
    display: grid;
    width: 100%;
    grid-template-columns: repeat(4, 1fr);
    gap: 15px;
    padding: 10px;
    box-sizing: border-box;
    background: #ffffff;
    border: 1px solid #e9edf3;
    border-radius: 12px;
  }

  .grid-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:active {
      .icon-wrapper {
        transform: scale(0.96);
      }
    }
  }

  .grid-item--disabled {
    cursor: default;

    &:active {
      .icon-wrapper {
        transform: none;
      }
    }
  }

  .icon-wrapper {
    display: flex;
    width: 40px;
    height: 40px;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
    overflow: hidden;
    transition: transform 0.16s ease;
  }

  .grid-icon {
    width: 40px;
    height: 40px;
    object-fit: contain;
  }

  .grid-text {
    max-width: 80px;
    overflow: hidden;
    color: #333333;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .my-swipe {
    width: 100%;
    height: 185px;
    margin-top: 10px;
    overflow: hidden;
    background: #f0f2f5;
    border-radius: 12px;

    .van-swipe-item {
      height: 185px;
      color: #ffffff;
      text-align: center;
      background: #f0f2f5;

      img {
        display: block;
        width: 100%;
        height: 185px;
        object-fit: cover;
      }
    }
  }

  .home-value-strip {
    display: flex;
    width: 100%;
    min-height: 30px;
    align-items: center;
    justify-content: space-between;
    margin: 5px 0 6px;
    color: #69717d;

    > div {
      display: flex;
      min-width: 0;
      align-items: center;
      justify-content: center;
      gap: 4px;
      font-size: 11px;
      line-height: 18px;
      white-space: nowrap;
    }

    img {
      width: 16px;
      height: 16px;
      flex: 0 0 16px;
      object-fit: contain;
    }
  }

  .home--english & .home-value-strip {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px 12px;
    padding: 4px 0;

    > div {
      justify-content: flex-start;
      font-size: 12px;
      white-space: normal;
      word-break: normal;
      overflow-wrap: normal;

      span { min-width: 0; }
    }
  }

  .home--english & .grid-text {
    max-width: 100%;
    overflow: visible;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .brand-showcase {
    position: relative;
    width: 100%;
    margin: 6px 0 10px;

    &.brand-showcase--can-scroll-right {
      .brand-showcase__viewport {
        -webkit-mask-image: linear-gradient(
          90deg,
          #000 0,
          #000 calc(100% - 32px),
          transparent 100%
        );
        mask-image: linear-gradient(
          90deg,
          #000 0,
          #000 calc(100% - 32px),
          transparent 100%
        );
      }
    }

    .brand-showcase__viewport {
      display: flex;
      width: 100%;
      gap: 8px;
      box-sizing: border-box;
      overflow-x: auto;
      overflow-y: hidden;
      overscroll-behavior-inline: contain;
      scroll-snap-type: x proximity;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }

      .brand-showcase__item {
        display: flex;
        width: 76px;
        height: 48px;
        flex: 0 0 76px;
        align-items: center;
        justify-content: center;
        padding: 8px;
        overflow: hidden;
        box-sizing: border-box;
        color: #4286f5;
        font-size: 16px;
        font-weight: 600;
        background: #ffffff;
        border: 1px solid #e9edf3;
        border-radius: 10px;
        scroll-snap-align: start;
        transition: border-color 0.16s ease, transform 0.16s ease;
        -webkit-tap-highlight-color: transparent;

        img {
          display: block;
          width: auto;
          height: auto;
          max-width: 60px;
          max-height: 28px;
          object-fit: contain;
        }

        &:active {
          border-color: #b9d1f7;
          transform: scale(0.97);
        }
      }
    }
  }

  @media (max-width: 359px) {
    .home-bg {
      right: -12px;
      left: -12px;
    }

    .grid-container {
      gap: 8px;
      padding: 8px;
    }

    .home-value-strip {
      > div {
        gap: 2px;
        font-size: 10px;
      }

      img {
        width: 14px;
        height: 14px;
        flex-basis: 14px;
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .grid-item .icon-wrapper,
    .brand-showcase__item {
      transition: none;
    }
  }
}

.home-showcase {
  box-sizing: border-box;
  width: calc(100% + 32px);
  padding: 8px 16px 16px;
  margin: 0 -16px;
  overflow: hidden;
}

.home-feed {
  position: relative;
  isolation: isolate;
  margin: 6px -16px -16px;
  padding: 0 16px 16px;
  background: linear-gradient(
    180deg,
    #ffffff 0,
    #f8fafc 24px,
    #f5f7fa 56px,
    #f5f7fa 100%
  );
  border-top: 0;

  .home-tab-bar {
    margin: 0 0 10px;
  }
}

.home-feed--standalone {
  margin-top: 2px;
  padding-top: 2px;
}

.home-tab-bar {
  display: flex;
  width: 100%;
  min-height: 43px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 14px 0 10px;
}

.home-tab-items {
  display: flex;
  width: auto;
  min-width: 0;
  min-height: 43px;
  flex: 1 1 auto;
  align-items: center;
  gap: 26px;
  padding: 0 3px;
  background: transparent;
}

.mobile-view-switch {
  display: flex;
  flex: 0 0 auto;
  gap: 5px;
  background: transparent;

  button {
    display: flex;
    width: 30px;
    height: 30px;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: #7c8798;
    font-size: 19px;
    background: transparent;
    border: 0;
    border-radius: 6px;
    -webkit-tap-highlight-color: transparent;

    &.active,
    &:active {
      color: #357ae8;
      background: transparent;
      box-shadow: none;
    }
  }
}

.home-tab-item {
  position: relative;
  display: flex;
  height: 43px;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  color: #7f8998;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  background: transparent;
  border: 0;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &::after {
    position: absolute;
    right: 4px;
    bottom: 0;
    left: 4px;
    height: 3px;
    background: #4286f5;
    border-radius: 2px 2px 0 0;
    opacity: 0;
    content: "";
    transform: scaleX(0.55);
    transition: opacity 0.18s ease, transform 0.18s ease;
  }

  &.active {
    color: #2f72dd;
    font-weight: 700;

    &::after {
      opacity: 1;
      transform: scaleX(1);
    }
  }
}

.van-list {
  width: 100%;
  padding-bottom: @bottom-bar-height;
}

.goods-waterfall-list {
  min-height: 240px;
}

.goods-waterfall {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding-bottom: 12px;

  ::v-deep .goods-item.goods-card {
    border-color: #e9edf3;
    box-shadow: none;

    &:active {
      box-shadow: none;
    }
  }
}

.goods-waterfall__column {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.goods-compact-list {
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding-bottom: 12px;
}

.goods-card-skeleton {
  box-sizing: border-box;
  overflow: hidden;
  padding-bottom: 10px;
  background: #ffffff;
  border: 1px solid #e9edf3;
  border-radius: 12px;
}

.goods-card-skeleton__image,
.goods-card-skeleton__line {
  background: linear-gradient(90deg, #f2f4f7 25%, #e9edf3 37%, #f2f4f7 63%);
  background-size: 400% 100%;
  animation: goods-skeleton-loading 1.4s ease infinite;
}

.goods-card-skeleton__image {
  width: 100%;
  aspect-ratio: 1 / 1;
}

.goods-card-skeleton__line {
  height: 12px;
  margin: 10px 10px 0;
  border-radius: 6px;
}

.goods-card-skeleton__line--title {
  width: calc(100% - 32px);
}

.goods-card-skeleton__line--short {
  width: 45%;
}

@keyframes goods-skeleton-loading {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

::v-deep .goods-waterfall-list {
  .van-list__loading,
  .van-list__finished-text,
  .van-list__error-text {
    height: 44px;
    color: #a0a8b5;
    font-size: 12px;
    line-height: 44px;
  }
}

.mobile-clue-section {
  position: relative;
  padding-bottom: 70px;
}

.mobile-clue-toolbar {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 4px;
  margin-bottom: 9px;
}

.mobile-clue-search {
  display: flex;
  height: 40px;
  flex: 1 1 auto;
  min-width: 0;
  align-items: center;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #e4e9f0;
  border-radius: 10px;

  input {
    flex: 1 1 auto;
    min-width: 0;
    height: 100%;
    padding: 0 4px 0 10px;
    color: #344054;
    font-size: 12px;
    background: transparent;
    border: 0;
    outline: 0;
    appearance: none;

    &::placeholder {
      color: #98a2b3;
    }

    &::-webkit-search-cancel-button {
      display: none;
    }
  }
}

.mobile-clue-search__submit,
.mobile-clue-search__filter,
.mobile-clue-search__clear {
  position: relative;
  display: flex;
  height: 40px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #7c8798;
  font-size: 16px;
  background: transparent;
  border: 0;
  -webkit-tap-highlight-color: transparent;

  &:active {
    color: #357ae8;
    background: #f4f7fb;
  }
}

.mobile-clue-search__submit {
  width: 34px;
  font-size: 17px;
}

.mobile-clue-search__filter {
  width: 40px;
  margin-right: 1px;
  color: #7c8798;
  font-size: 17px;

  &::after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 1px;
    background: #e4e9f0;
    content: "";
  }

  &.active {
    color: #357ae8;
  }

  sup {
    position: absolute;
    top: 5px;
    right: 4px;
    min-width: 13px;
    height: 13px;
    padding: 0 2px;
    color: #ffffff;
    font-size: 8px;
    font-weight: 600;
    line-height: 13px;
    text-align: center;
    background: #4286f5;
    border-radius: 7px;
  }
}

.mobile-clue-search__clear {
  width: 28px;
  font-size: 13px;
}

.mobile-clue-import {
  display: flex;
  height: 40px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 0 11px;
  color: #69717d;
  font-size: 12px;
  font-weight: 500;
  background: #ffffff;
  border: 1px solid #e4e9f0;
  border-radius: 10px;
  -webkit-tap-highlight-color: transparent;

  &:active {
    color: #475467;
    background: #f8fafc;
  }
}

.mobile-filter-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 1px 9px;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  span {
    height: 25px;
    flex: 0 0 auto;
    padding: 0 9px;
    color: #357ae8;
    font-size: 10px;
    line-height: 25px;
    background: #edf4ff;
    border-radius: 13px;
  }

  button {
    height: 25px;
    flex: 0 0 auto;
    padding: 0 4px;
    color: #8b95a5;
    font-size: 10px;
    background: transparent;
    border: 0;
  }
}

.mobile-filter-popover {
  position: absolute;
  top: 47px;
  right: 0;
  left: 0;
  z-index: 1500;
  overflow: visible;
  background: #ffffff;
  border: 1px solid #e4e9f0;
  border-radius: 14px;
  box-shadow: 0 10px 28px rgba(31, 41, 55, 0.14);

  &::before {
    position: absolute;
    top: -6px;
    left: 15px;
    width: 11px;
    height: 11px;
    background: #ffffff;
    border-top: 1px solid #e4e9f0;
    border-left: 1px solid #e4e9f0;
    content: "";
    transform: rotate(45deg);
  }
}

.mobile-filter-popover__heading {
  position: relative;
  z-index: 1;
  display: flex;
  height: 45px;
  align-items: center;
  justify-content: space-between;
  padding: 0 13px 0 14px;
  background: #ffffff;
  border-radius: 14px 14px 0 0;

  strong {
    color: #273142;
    font-size: 14px;
    font-weight: 600;
  }

  button {
    display: flex;
    width: 30px;
    height: 30px;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: #8b95a5;
    font-size: 15px;
    background: transparent;
    border: 0;
  }
}

.mobile-filter-popover__content {
  max-height: min(360px, 50vh);
  padding: 1px 14px 14px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.mobile-filter-popover__section {
  & + & {
    margin-top: 16px;
  }

  h3 {
    margin: 0 0 9px;
    color: #475467;
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
  }
}

.mobile-filter-option-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;

  button {
    min-width: 0;
    height: 34px;
    padding: 0 7px;
    overflow: hidden;
    color: #667085;
    font-size: 11px;
    line-height: 32px;
    text-overflow: ellipsis;
    white-space: nowrap;
    background: #ffffff;
    border: 1px solid #e4e7ec;
    border-radius: 8px;

    &.active {
      color: #357ae8;
      font-weight: 600;
      background: #edf4ff;
      border-color: #8cb8fb;
    }

    &.mobile-filter-option-more {
      color: #357ae8;
      background: #f7faff;
      border-style: dashed;
    }
  }
}

.mobile-filter-popover__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding: 10px 14px 13px;
  background: #ffffff;
  border-top: 1px solid #eef1f5;
  border-radius: 0 0 14px 14px;

  button {
    height: 36px;
    color: #667085;
    font-size: 12px;
    font-weight: 500;
    background: #ffffff;
    border: 1px solid #dfe4eb;
    border-radius: 8px;

    &.primary {
      color: #ffffff;
      background: #4286f5;
      border-color: #4286f5;
    }
  }
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0;
  background: transparent;
}

.info-state-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 18px 0 32px;
  background: #ffffff;
  border-radius: 12px;

  ::v-deep .van-empty {
    padding-bottom: 12px;
  }
}

.info-item {
  .info-publisher-chat { display: flex; justify-content: flex-end; margin-top: 4px; }
  box-sizing: border-box;
  width: 100%;
  padding: 10px 11px;
  color: #344054;
  background: #ffffff;
  border: 1px solid #e9edf3;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(30, 56, 96, 0.04);
  -webkit-tap-highlight-color: transparent;

  &:active {
    background: #f9fbff;
    transform: scale(0.995);
  }
}

.info-header {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 0 11px 7px;
  margin: 0 -11px;
  border-bottom: 1px solid #f0f2f5;
}

.info-header-content {
  display: flex;
  align-items: baseline;
  gap: 16px;
  flex: 1;
  min-width: 0;

  strong,
  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    flex: 0 1 auto;
    min-width: 0;
  }

  span {
    flex: 0 0 auto;
    max-width: 40%;
    margin-left: auto;
    margin-top: 0;
    color: #1f2937;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
  }
}

.info-brand {
  color: #1f2937;
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
}

.info-checkbox {
  flex: 0 0 20px;
  margin-left: auto;

  ::v-deep .van-checkbox__icon--square .van-icon {
    border-radius: 4px;
  }
}

.info-body {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 4px 8px;
  padding-top: 7px;
}

.info-row {
  display: flex;
  min-width: 0;
  align-items: baseline;
  gap: 3px;
}

.info-label {
  flex: 0 0 auto;
  color: #98a2b3;
  font-size: 9px;
  line-height: 15px;
}

.info-value {
  min-width: 0;
  overflow: hidden;
  color: #344054;
  font-size: 10px;
  line-height: 15px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.info-trade-row,
.info-footer {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-top: 7px;
  color: #667085;
  font-size: 10px;
  line-height: 16px;

  > span {
    display: flex;
    min-width: 0;
    align-items: baseline;
    gap: 3px;
  }

  small {
    flex: 0 0 auto;
    color: #98a2b3;
    font-size: 9px;
  }
}

.info-trade-row {
  strong {
    color: #4286f5;
    font-size: 11px;
  }
}

.info-footer {
  flex-wrap: wrap;
  gap: 3px 12px;
  margin-top: 6px;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.selected-action-bar {
  position: fixed;
  right: 12px;
  bottom: calc(@bottom-bar-height + 12px + env(safe-area-inset-bottom));
  left: 12px;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 11px 10px 14px;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(66, 134, 245, 0.12);
  border-radius: 14px;
  box-shadow: 0 8px 28px rgba(27, 49, 86, 0.18);
  backdrop-filter: blur(10px);
}

.selected-action-bar-text {
  display: flex;
  flex-direction: column;

  strong {
    color: #4286f5;
    font-size: 18px;
    line-height: 21px;
  }

  span {
    color: #7c8798;
    font-size: 10px;
    line-height: 15px;
  }
}

.selected-action-bar__buttons {
  display: flex;
  gap: 8px;

  .van-button {
    height: 34px;
    padding: 0 15px;
    font-size: 12px;
    border-radius: 9px;
  }
}

.import-clue-dialog {
  padding: 10px 18px 18px;
}

.import-clue-dialog__handle {
  width: 36px;
  height: 4px;
  margin: 0 auto 14px;
  background: #d7dce4;
  border-radius: 2px;
}

.import-clue-dialog__heading {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;

  h2 {
    margin: 0;
    color: #1f2937;
    font-size: 16px;
    line-height: 23px;
  }

  p {
    margin: 2px 0 0;
    color: #98a2b3;
    font-size: 10px;
    line-height: 16px;
  }
}

.import-clue-dialog__icon {
  display: flex;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  align-items: center;
  justify-content: center;
  color: #4286f5;
  font-size: 21px;
  background: #edf4ff;
  border-radius: 11px;
}

::v-deep .import-clue-uploader {
  display: block;
  margin-bottom: 16px;

  .van-uploader__wrapper,
  .van-uploader__input-wrapper,
  .van-uploader__upload {
    width: 100%;
  }

  .van-uploader__upload {
    height: 100px;
    margin: 0;
    background: #f7f9fc;
    border: 1px dashed #cbd5e1;
    border-radius: 11px;
  }

  .van-uploader__upload-icon {
    color: #7f95b4;
    font-size: 25px;
  }

  .van-uploader__upload-text {
    margin-top: 5px;
    color: #7c8798;
    font-size: 11px;
  }

  .van-uploader__preview {
    margin: 0;
  }
}

.upload-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  .van-button {
    height: 40px;
    font-size: 12px;
    border-radius: 10px;
  }

  .van-button--primary {
    background: #4286f5;
    border-color: #4286f5;
  }
}

@media (max-width: 359px) {
  .main-container {
    padding-right: 12px;
    padding-left: 12px;
  }

  .home-showcase {
    width: calc(100% + 24px);
    padding-right: 12px;
    padding-left: 12px;
    margin-right: -12px;
    margin-left: -12px;
  }

  .home-feed {
    margin-right: -12px;
    margin-left: -12px;
    padding-right: 12px;
    padding-left: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .home-tab-item {
    transition: none;
  }
}
</style>

<style lang="less">
.import-clue-popup.van-popup--bottom {
  right: 0;
  left: 0;
  z-index: 3000 !important;
  width: 100%;
  max-width: 700px;
  max-height: 78vh;
  margin: 0 auto;
  overflow-x: hidden;
  overflow-y: auto;
  border-radius: 18px 18px 0 0 !important;
  transform: translate3d(0, 0, 0);
  -webkit-transform: translate3d(0, 0, 0);
}
</style>
